import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Search, SlidersHorizontal, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { FormAccordionCard, FormSectionHeader } from '@/components/molecules';
import { CustomFieldValueInput } from '@/components/organisms/custom-fields/CustomFieldValueInput';
import { getInitialCustomFieldDraftValue } from '@/components/organisms/custom-fields/custom-field-form-utils';
import type { CustomFieldSchema } from '@/types/custom-fields';

interface SortableCustomFieldRowProps {
  fieldKey: string;
  schema: CustomFieldSchema;
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}

function SortableCustomFieldRow({
  fieldKey,
  schema,
  value,
  onChange,
  onRemove,
}: SortableCustomFieldRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldKey,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-md border bg-background px-2 py-2"
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground/40 hover:text-muted-foreground"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} />
      </button>

      {schema.definition.fieldType === 'CHECKBOX' ? (
        <div className="w-36 shrink-0" />
      ) : (
        <Label htmlFor={`cf-${schema.definition.id}`} className="w-36 shrink-0 text-sm">
          {schema.definition.label}
          {schema.association.isRequired && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
      )}

      <div className="flex-1">
        <CustomFieldValueInput
          id={`cf-${schema.definition.id}`}
          schema={schema}
          value={value}
          onChange={onChange}
          required={schema.association.isRequired}
        />
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
        disabled={schema.association.isRequired}
        onClick={onRemove}
        title={schema.association.isRequired ? 'Required field cannot be removed' : 'Remove field'}
      >
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  );
}

interface EstimateCustomFieldsSectionProps {
  open: boolean;
  onToggle: () => void;
  bare?: boolean;
  schemas: CustomFieldSchema[];
  activeFieldKeys: string[];
  values: Record<string, string>;
  onFieldChange: (fieldKey: string, value: string) => void;
  onActiveFieldKeysChange: (keys: string[]) => void;
  onRemoveField: (fieldKey: string) => void;
  emptyState?: ReactNode;
}

export function EstimateCustomFieldsSection({
  open,
  onToggle,
  bare = false,
  schemas,
  activeFieldKeys,
  values,
  onFieldChange,
  onActiveFieldKeysChange,
  onRemoveField,
  emptyState,
}: EstimateCustomFieldsSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearch(false);
        setSearchQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const availableSchemas = useMemo(
    () =>
      schemas.filter(
        (schema) =>
          !activeFieldKeys.includes(schema.definition.fieldKey) &&
          schema.definition.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [activeFieldKeys, schemas, searchQuery],
  );

  const extra = schemas.some((schema) => !activeFieldKeys.includes(schema.definition.fieldKey)) ? (
    <div ref={searchRef} className="relative">
      {showSearch ? (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-8 pl-7 pr-2 text-xs"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowSearch(true)}
          className="flex h-8 items-center gap-1 rounded-md border border-dashed px-2 text-xs text-muted-foreground hover:bg-accent"
        >
          <Plus className="size-3" />
          Add field
        </button>
      )}
      {showSearch && (
        <div className="absolute right-0 z-50 mt-1 max-h-52 w-48 overflow-auto rounded-md border bg-popover shadow-md">
          {availableSchemas.map((schema) => (
            <button
              key={schema.definition.id}
              type="button"
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent"
              onMouseDown={(event) => {
                event.preventDefault();
                const fieldKey = schema.definition.fieldKey;
                onActiveFieldKeysChange([...activeFieldKeys, fieldKey]);
                if (values[fieldKey] === undefined) {
                  onFieldChange(fieldKey, getInitialCustomFieldDraftValue(schema));
                }
                setSearchQuery('');
              }}
            >
              <span>
                {schema.definition.label}
                {schema.association.isRequired && (
                  <span className="ml-1 text-xs text-muted-foreground">*</span>
                )}
              </span>
              <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
          ))}
          {availableSchemas.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No fields found</p>
          )}
        </div>
      )}
    </div>
  ) : undefined;

  const body = (
    <>
      {activeFieldKeys.length === 0 ? (
        (emptyState ?? (
          <p className="text-sm text-muted-foreground">
            No fields added yet. Use &ldquo;Add field&rdquo; to include custom fields.
          </p>
        ))
      ) : (
        <DndContext
          sensors={sensors}
          onDragEnd={(event: DragEndEvent) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
              const oldIndex = activeFieldKeys.indexOf(active.id as string);
              const newIndex = activeFieldKeys.indexOf(over.id as string);
              onActiveFieldKeysChange(arrayMove(activeFieldKeys, oldIndex, newIndex));
            }
          }}
        >
          <SortableContext items={activeFieldKeys} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {activeFieldKeys.map((fieldKey) => {
                const schema = schemas.find((item) => item.definition.fieldKey === fieldKey);
                if (!schema) return null;
                return (
                  <SortableCustomFieldRow
                    key={fieldKey}
                    fieldKey={fieldKey}
                    schema={schema}
                    value={values[fieldKey] ?? ''}
                    onChange={(value) => onFieldChange(fieldKey, value)}
                    onRemove={() => onRemoveField(fieldKey)}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </>
  );

  if (bare) {
    return (
      <div className="space-y-3">
        {extra ? <div className="flex justify-end">{extra}</div> : null}
        {body}
      </div>
    );
  }

  return (
    <FormAccordionCard
      open={open}
      header={
        <FormSectionHeader
          icon={SlidersHorizontal}
          title="Custom fields"
          open={open}
          onToggle={onToggle}
          statusBadge={activeFieldKeys.length > 0 ? 'Set' : undefined}
          extra={extra}
        />
      }
    >
      {body}
    </FormAccordionCard>
  );
}
